import { c as _c } from "react/compiler-runtime";
import { Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Invoice } from "@/api";
import { Button } from "@osmix/ui/button";

// Формат суммы в рублях для счёта — счёт всегда в валюте расчёта (руб.),
// без конвертации в отображаемую валюту витрины.
const rub = (value: number): string => `${new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(value)} руб.`;
type InvoiceLabels = {
  title: string;
  issuedAt: string;
  orderNumber: string;
  seller: string;
  buyer: string;
  items: string;
  itemName: string;
  qty: string;
  unitPrice: string;
  lineTotal: string;
  subtotal: string;
  delivery: string;
  total: string;
  inn: string;
  kpp: string;
  ogrn: string;
  legalAddress: string;
  bankName: string;
  bankAccount: string;
  corrAccount: string;
  bik: string;
};

// Печать счёта: открываем изолированное окно с автономным HTML и вызываем
// печать браузера (подход «PDF из HTML» — пользователь сохраняет в PDF из
// диалога печати). Так на печать не попадают шапка и футер витрины.
const printInvoice = (invoice: Invoice, labels: InvoiceLabels): void => {
  const row = (label: string, value: string) => value ? `<tr><td class="l">${label}</td><td>${value}</td></tr>` : "";
  const req = (r: Invoice["buyer"] | Invoice["platform"]) => [row(labels.inn, r.inn), row(labels.kpp, r.kpp), row(labels.ogrn, "ogrn" in r ? r.ogrn : ""), row(labels.legalAddress, r.legalAddress), row(labels.bankName, r.bankName), row(labels.bankAccount, r.bankAccount), row(labels.corrAccount, r.corrAccount), row(labels.bik, r.bik)].join("");
  const items = invoice.items.map((i, idx) => `<tr><td>${idx + 1}</td><td>${i.name}<br><span class="muted">${i.shopName}</span></td><td class="r">${i.qty}</td><td class="r">${rub(i.unitPrice)}</td><td class="r">${rub(i.lineTotal)}</td></tr>`).join("");
  const html = `<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>${labels.title} ${invoice.number}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:32px;font-size:12px;line-height:1.45}
h1{font-size:18px;margin:0 0 4px}h2{font-size:13px;margin:20px 0 6px}
.muted{color:#666;font-size:11px}
.cols{display:flex;gap:32px;margin-top:12px}.cols>div{flex:1}
table{width:100%;border-collapse:collapse;margin-top:8px}
td,th{padding:6px 8px;border:1px solid #ddd;vertical-align:top}
th{background:#f4f4f4;text-align:left}
td.l{color:#666;width:42%}td.r,th.r{text-align:right}
.totals{margin-top:12px;width:auto;margin-left:auto}
.totals td{border:none;padding:3px 8px}.totals .grand{font-weight:bold;font-size:14px}
</style></head><body>
<h1>${labels.title} ${invoice.number}</h1>
<p class="muted">${labels.issuedAt}: ${new Date(invoice.issuedAt).toLocaleString("ru-RU")} · ${labels.orderNumber}: ${invoice.orderId}</p>
<div class="cols">
<div><h2>${labels.seller}</h2><p><b>${invoice.platform.companyName}</b></p><table>${req(invoice.platform)}</table></div>
<div><h2>${labels.buyer}</h2><p><b>${invoice.buyer.companyName}</b></p><table>${req(invoice.buyer)}</table></div>
</div>
<h2>${labels.items}</h2>
<table><thead><tr><th>#</th><th>${labels.itemName}</th><th class="r">${labels.qty}</th><th class="r">${labels.unitPrice}</th><th class="r">${labels.lineTotal}</th></tr></thead><tbody>${items}</tbody></table>
<table class="totals">
<tr><td>${labels.subtotal}</td><td class="r">${rub(invoice.subtotal)}</td></tr>
<tr><td>${labels.delivery}</td><td class="r">${rub(invoice.deliveryTotal)}</td></tr>
<tr class="grand"><td>${labels.total}</td><td class="r">${rub(invoice.total)}</td></tr>
</table>
</body></html>`;
  const win = window.open("", "_blank", "width=900,height=1200");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  // Ждём отрисовки перед печатью, иначе диалог печати откроется на пустой странице.
  setTimeout(() => win.print(), 300);
};

// Подписи счёта (общие для просмотра и печати).
const buildInvoiceLabels = (t: (key: string) => string): InvoiceLabels => ({
  title: t("invoice.title"),
  issuedAt: t("invoice.issuedAt"),
  orderNumber: t("invoice.orderNumber"),
  seller: t("invoice.seller"),
  buyer: t("invoice.buyer"),
  items: t("invoice.items"),
  itemName: t("invoice.itemName"),
  qty: t("invoice.qty"),
  unitPrice: t("invoice.unitPrice"),
  lineTotal: t("invoice.lineTotal"),
  subtotal: t("invoice.subtotal"),
  delivery: t("invoice.delivery"),
  total: t("invoice.total"),
  inn: t("invoice.inn"),
  kpp: t("invoice.kpp"),
  ogrn: t("invoice.ogrn"),
  legalAddress: t("invoice.legalAddress"),
  bankName: t("invoice.bankName"),
  bankAccount: t("invoice.bankAccount"),
  corrAccount: t("invoice.corrAccount"),
  bik: t("invoice.bik")
});

// Печать/сохранение счёта в PDF без виджета просмотра (кнопка «Скачать» в
// модалке «Счёт №…» на оформлении заказа).
export function useInvoicePrinter() {
  const $ = _c(2);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = invoice => printInvoice(invoice, buildInvoiceLabels(t));
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

// Просмотр счёта по заказу «оплата по счёту» (fd-payment-02). Реквизиты
// покупателя (ЮЛ) + площадки + позиции + суммы; кнопка печати/сохранения в PDF.
export function InvoiceView(t0) {
  const $ = _c(107);
  const {
    invoice
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = buildInvoiceLabels(t);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const labels = t1;
  let t2;
  if ($[2] !== labels.bankAccount || $[3] !== labels.bankName || $[4] !== labels.bik || $[5] !== labels.corrAccount || $[6] !== labels.inn || $[7] !== labels.kpp || $[8] !== labels.legalAddress || $[9] !== labels.ogrn) {
    t2 = t3 => {
      const {
        r
      } = t3;
      return <dl className="mt-2 flex flex-col gap-1 type-small text-osmix-text-2">{r.inn && <RowKV k={labels.inn} v={r.inn} />}{r.kpp && <RowKV k={labels.kpp} v={r.kpp} />}{"ogrn" in r && r.ogrn && <RowKV k={labels.ogrn} v={r.ogrn} />}{r.legalAddress && <RowKV k={labels.legalAddress} v={r.legalAddress} />}{r.bankName && <RowKV k={labels.bankName} v={r.bankName} />}{r.bankAccount && <RowKV k={labels.bankAccount} v={r.bankAccount} />}{r.corrAccount && <RowKV k={labels.corrAccount} v={r.corrAccount} />}{r.bik && <RowKV k={labels.bik} v={r.bik} />}</dl>;
    };
    $[2] = labels.bankAccount;
    $[3] = labels.bankName;
    $[4] = labels.bik;
    $[5] = labels.corrAccount;
    $[6] = labels.inn;
    $[7] = labels.kpp;
    $[8] = labels.legalAddress;
    $[9] = labels.ogrn;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const Req = t2;
  let t3;
  if ($[11] !== invoice.number || $[12] !== labels.title) {
    t3 = <h3 className="type-h4 text-osmix-text-1">{labels.title} {invoice.number}</h3>;
    $[11] = invoice.number;
    $[12] = labels.title;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = labels.issuedAt;
  let t5;
  if ($[14] !== invoice.issuedAt) {
    t5 = new Date(invoice.issuedAt).toLocaleString("ru-RU");
    $[14] = invoice.issuedAt;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== labels.issuedAt || $[17] !== t5) {
    t6 = <p className="type-small text-osmix-icon">{t4}: {t5}</p>;
    $[16] = labels.issuedAt;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t3 || $[20] !== t6) {
    t7 = <div>{t3}{t6}</div>;
    $[19] = t3;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== invoice || $[23] !== labels) {
    t8 = () => printInvoice(invoice, labels);
    $[22] = invoice;
    $[23] = labels;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Printer className="mr-1 size-4" />;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t("invoice.print");
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t8) {
    t11 = <Button type="button" variant="osmix-outline" size="pill-sm" onClick={t8}>{t9}{t10}</Button>;
    $[28] = t10;
    $[29] = t8;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t7) {
    t12 = <header className="flex flex-wrap items-start justify-between gap-3 border-b border-osmix-gray-300 pb-4">{t7}{t11}</header>;
    $[31] = t11;
    $[32] = t7;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== labels.seller) {
    t13 = <p className="type-body2-bold text-osmix-text-1">{labels.seller}</p>;
    $[34] = labels.seller;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== invoice.platform.companyName) {
    t14 = <p className="type-body2 text-osmix-text-1">{invoice.platform.companyName}</p>;
    $[36] = invoice.platform.companyName;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== Req || $[39] !== invoice.platform) {
    t15 = <Req r={invoice.platform} />;
    $[38] = Req;
    $[39] = invoice.platform;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t13 || $[42] !== t14 || $[43] !== t15) {
    t16 = <div>{t13}{t14}{t15}</div>;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== labels.buyer) {
    t17 = <p className="type-body2-bold text-osmix-text-1">{labels.buyer}</p>;
    $[45] = labels.buyer;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== invoice.buyer.companyName) {
    t18 = <p className="type-body2 text-osmix-text-1">{invoice.buyer.companyName}</p>;
    $[47] = invoice.buyer.companyName;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== Req || $[50] !== invoice.buyer) {
    t19 = <Req r={invoice.buyer} />;
    $[49] = Req;
    $[50] = invoice.buyer;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t17 || $[53] !== t18 || $[54] !== t19) {
    t20 = <div>{t17}{t18}{t19}</div>;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== t16 || $[57] !== t20) {
    t21 = <div className="grid gap-4 py-4 sm:grid-cols-2">{t16}{t20}</div>;
    $[56] = t16;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== labels.itemName) {
    t22 = <th className="py-2 pr-2 font-bold">{labels.itemName}</th>;
    $[59] = labels.itemName;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== labels.qty) {
    t23 = <th className="py-2 px-2 text-right font-bold">{labels.qty}</th>;
    $[61] = labels.qty;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== labels.unitPrice) {
    t24 = <th className="py-2 px-2 text-right font-bold">{labels.unitPrice}</th>;
    $[63] = labels.unitPrice;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== labels.lineTotal) {
    t25 = <th className="py-2 pl-2 text-right font-bold">{labels.lineTotal}</th>;
    $[65] = labels.lineTotal;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== t22 || $[68] !== t23 || $[69] !== t24 || $[70] !== t25) {
    t26 = <thead><tr className="border-b border-osmix-gray-300 text-left text-osmix-text-2">{t22}{t23}{t24}{t25}</tr></thead>;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== invoice.items) {
    t27 = invoice.items.map(_temp);
    $[72] = invoice.items;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== t27) {
    t28 = <tbody>{t27}</tbody>;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== t26 || $[77] !== t28) {
    t29 = <div className="overflow-x-auto"><table className="w-full border-collapse type-small">{t26}{t28}</table></div>;
    $[76] = t26;
    $[77] = t28;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  const t30 = labels.subtotal;
  let t31;
  if ($[79] !== invoice.subtotal) {
    t31 = rub(invoice.subtotal);
    $[79] = invoice.subtotal;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== labels.subtotal || $[82] !== t31) {
    t32 = <RowKV k={t30} v={t31} />;
    $[81] = labels.subtotal;
    $[82] = t31;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  const t33 = labels.delivery;
  let t34;
  if ($[84] !== invoice.deliveryTotal) {
    t34 = rub(invoice.deliveryTotal);
    $[84] = invoice.deliveryTotal;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== labels.delivery || $[87] !== t34) {
    t35 = <RowKV k={t33} v={t34} />;
    $[86] = labels.delivery;
    $[87] = t34;
    $[88] = t35;
  } else {
    t35 = $[88];
  }
  let t36;
  if ($[89] !== labels.total) {
    t36 = <dt className="type-body1-bold text-osmix-text-1">{labels.total}</dt>;
    $[89] = labels.total;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== invoice.total) {
    t37 = rub(invoice.total);
    $[91] = invoice.total;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  let t38;
  if ($[93] !== t37) {
    t38 = <dd className="type-body1-bold text-osmix-text-1">{t37}</dd>;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== t36 || $[96] !== t38) {
    t39 = <div className="mt-1 flex items-center justify-between border-t border-osmix-gray-300 pt-2">{t36}{t38}</div>;
    $[95] = t36;
    $[96] = t38;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] !== t32 || $[99] !== t35 || $[100] !== t39) {
    t40 = <dl className="ml-auto mt-4 flex w-full max-w-xs flex-col gap-1 type-body2">{t32}{t35}{t39}</dl>;
    $[98] = t32;
    $[99] = t35;
    $[100] = t39;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== t12 || $[103] !== t21 || $[104] !== t29 || $[105] !== t40) {
    t41 = <article className="w-full text-left" data-testid="invoice-view">{t12}{t21}{t29}{t40}</article>;
    $[102] = t12;
    $[103] = t21;
    $[104] = t29;
    $[105] = t40;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  return t41;
}
function _temp(i, idx) {
  return <tr key={`${i.name}-${idx}`} className="border-b border-osmix-gray-200"><td className="py-2 pr-2 text-osmix-text-1">{i.name}<br /><span className="text-osmix-icon">{i.shopName}</span></td><td className="py-2 px-2 text-right">{i.qty}</td><td className="py-2 px-2 text-right">{rub(i.unitPrice)}</td><td className="py-2 pl-2 text-right">{rub(i.lineTotal)}</td></tr>;
}
function RowKV(t0) {
  const $ = _c(7);
  const {
    k,
    v
  } = t0;
  let t1;
  if ($[0] !== k) {
    t1 = <dt className="text-osmix-text-2">{k}</dt>;
    $[0] = k;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== v) {
    t2 = <dd className="text-right text-osmix-text-1">{v}</dd>;
    $[2] = v;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-start justify-between gap-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}